// src/pages/admin/reports/NegativeStockReport.jsx
// Read-only report of products and raw materials with negative (deficit) stock.
// Cashiers: view only.  Managers + Admins: view + adjust stock.

import React, { useState, useEffect, useCallback } from 'react'
import {
  RefreshCw,
  AlertTriangle,
  Package,
  FlaskConical,
  Layers,
  Zap,
  ChevronDown,
  X,
  Loader2,
  SlidersHorizontal,
  Clock,
  CheckCircle2,
  TrendingDown,
} from 'lucide-react'
import { format } from 'date-fns'
import toast from 'react-hot-toast'
import BranchService from '../../../services/branchService'
import AuthService from '../../../services/authService'
import {
  getNegativeStockReport,
  adjustProductStock,
  adjustRawMaterialStock,
} from '../../../services/stockBalanceService'
import { PERMISSIONS, hasPermission } from '../../../config/roles'

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
const fmtDate = (iso) => {
  if (!iso) return '—'
  try {
    return format(new Date(iso), 'MMM d, yyyy  h:mm a')
  } catch {
    return '—'
  }
}

const RoleBadge = ({ role }) => {
  const colors = {
    admin: 'bg-purple-100 text-purple-700',
    super_admin: 'bg-purple-100 text-purple-700',
    manager: 'bg-blue-100 text-blue-700',
    cashier: 'bg-green-100 text-green-700',
    barista: 'bg-orange-100 text-orange-700',
  }
  return (
    <span
      className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${colors[role] || 'bg-gray-100 text-gray-600'}`}
    >
      {role}
    </span>
  )
}

// ---------------------------------------------------------------------------
// Summary cards
// ---------------------------------------------------------------------------
const SummaryCards = ({ summary }) => {
  const cards = [
    {
      id: 'neg-products',
      label: 'Negative Products',
      value: summary?.totalNegativeProducts ?? 0,
      icon: Package,
      color: 'from-red-500 to-rose-600',
      bg: 'bg-red-50',
      text: 'text-red-600',
      alert: (summary?.totalNegativeProducts ?? 0) > 0,
    },
    {
      id: 'neg-raw',
      label: 'Negative Raw Materials',
      value: summary?.totalNegativeRawMaterials ?? 0,
      icon: FlaskConical,
      color: 'from-orange-500 to-amber-600',
      bg: 'bg-orange-50',
      text: 'text-orange-600',
      alert: (summary?.totalNegativeRawMaterials ?? 0) > 0,
    },
    {
      id: 'deficit-batches',
      label: 'DEFICIT Batches',
      value:
        (summary?.totalDeficitProductBatches ?? 0) +
        (summary?.totalDeficitRawMaterialBatches ?? 0),
      icon: Layers,
      color: 'from-yellow-500 to-orange-500',
      bg: 'bg-yellow-50',
      text: 'text-yellow-600',
      alert:
        (summary?.totalDeficitProductBatches ?? 0) +
          (summary?.totalDeficitRawMaterialBatches ?? 0) >
        0,
    },
    {
      id: 'force-punched',
      label: 'Force-Punched Orders',
      value: summary?.totalForcePunchedOrders ?? 0,
      icon: Zap,
      color: 'from-violet-500 to-purple-600',
      bg: 'bg-violet-50',
      text: 'text-violet-600',
      alert: false,
    },
  ]

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
      {cards.map((card) => {
        const Icon = card.icon
        return (
          <div
            key={card.id}
            className="bg-white rounded-xl border border-gray-100 shadow-sm p-5 flex items-center gap-4 relative overflow-hidden"
          >
            <div
              className={`flex-shrink-0 w-12 h-12 rounded-full ${card.bg} flex items-center justify-center`}
            >
              <Icon className={`w-6 h-6 ${card.text}`} />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide leading-tight">
                {card.label}
              </p>
              <div className="flex items-center gap-2 mt-1">
                <p className={`text-2xl font-bold ${card.text}`}>{card.value}</p>
                {card.alert && card.value > 0 && (
                  <span className="flex-shrink-0 w-5 h-5 bg-red-100 rounded-full flex items-center justify-center">
                    <AlertTriangle className="w-3 h-3 text-red-500" />
                  </span>
                )}
              </div>
            </div>
            <div
              className={`absolute right-0 top-0 bottom-0 w-1 bg-gradient-to-b ${card.color} opacity-70`}
            />
          </div>
        )
      })}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Adjust Stock Modal
// ---------------------------------------------------------------------------
const AdjustStockModal = ({
  isOpen,
  onClose,
  item,      // { type: 'product'|'rawMaterial', id, name, currentStock, unit, branchId }
  onSuccess, // called after successful adjustment
}) => {
  const [actualCount, setActualCount] = useState('')
  const [loading, setLoading] = useState(false)
  const [errorMsg, setErrorMsg] = useState('')

  // Reset form when item changes
  useEffect(() => {
    setActualCount('')
    setErrorMsg('')
  }, [item])

  if (!isOpen || !item) return null

  const handleClose = () => {
    if (loading) return
    setErrorMsg('')
    onClose?.()
  }

  const handleSave = async () => {
    const count = parseFloat(actualCount)
    if (actualCount === '' || isNaN(count) || count < 0) {
      setErrorMsg('Please enter a valid non-negative number.')
      return
    }

    setLoading(true)
    setErrorMsg('')
    try {
      if (item.type === 'product') {
        await adjustProductStock({
          branchId: item.branchId,
          productId: item.id,
          actualCount: count,
        })
      } else {
        await adjustRawMaterialStock({
          branchId: item.branchId,
          rawMaterialId: item.id,
          actualCount: count,
        })
      }

      toast.success('Stock adjusted successfully', {
        icon: <CheckCircle2 className="w-4 h-4 text-green-500" />,
      })
      handleClose()
      onSuccess?.()
    } catch (err) {
      setErrorMsg(err?.message || 'Failed to adjust stock. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const unitLabel = item.unit?.abbreviation || item.unit?.name || ''

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.55)' }}
      onClick={(e) => { if (e.target === e.currentTarget) handleClose() }}
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 bg-gradient-to-r from-orange-500 to-amber-500 text-white">
          <div className="flex items-center gap-3">
            <SlidersHorizontal className="w-5 h-5" />
            <h2 className="text-base font-bold leading-tight">
              Adjust Stock — <span className="font-normal">{item.name}</span>
            </h2>
          </div>
          <button
            onClick={handleClose}
            disabled={loading}
            className="p-1.5 rounded-lg hover:bg-white/20 transition-colors disabled:opacity-50"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 pt-5 pb-2 space-y-4">
          {/* Info */}
          <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 text-sm text-blue-700 leading-relaxed">
            Enter the actual physical count from your inventory check. This will correct the stock
            level and clear any deficit records.
          </div>

          {/* Current stock */}
          <div className="bg-gray-50 rounded-xl p-4">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
              Current Stock
            </p>
            <p className={`text-xl font-bold ${item.currentStock < 0 ? 'text-red-600' : 'text-gray-800'}`}>
              {item.currentStock} {unitLabel}
            </p>
          </div>

          {/* Input */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1.5">
              Actual Physical Count{unitLabel ? ` (${unitLabel})` : ''}
            </label>
            <input
              id="adjust-stock-count-input"
              type="number"
              min="0"
              step="any"
              value={actualCount}
              onChange={(e) => { setActualCount(e.target.value); setErrorMsg('') }}
              placeholder="e.g. 10"
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-orange-400 text-lg font-semibold"
              autoFocus
            />
          </div>

          {errorMsg && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-sm text-red-700">
              {errorMsg}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-5 flex gap-3">
          <button
            onClick={handleClose}
            disabled={loading}
            className="flex-1 px-4 py-3 rounded-xl border border-gray-300 text-gray-700 font-semibold text-sm hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            id="adjust-stock-save-btn"
            onClick={handleSave}
            disabled={loading}
            className="flex-1 px-4 py-3 rounded-xl bg-gradient-to-r from-orange-500 to-amber-500 text-white font-bold text-sm flex items-center justify-center gap-2 shadow-md hover:from-orange-600 hover:to-amber-600 transition-all disabled:opacity-60"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>Saving…</span>
              </>
            ) : (
              'Save Adjustment'
            )}
          </button>
        </div>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Table components
// ---------------------------------------------------------------------------
const EmptyRow = ({ cols, message }) => (
  <tr>
    <td colSpan={cols} className="text-center py-10 text-gray-400 text-sm">
      {message}
    </td>
  </tr>
)

const thClass = 'text-left text-xs font-semibold text-gray-500 uppercase tracking-wide py-3 px-4 bg-gray-50 border-b border-gray-200'
const tdClass = 'py-3 px-4 text-sm text-gray-700 border-b border-gray-100'

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------
const NegativeStockReport = () => {
  const user = AuthService.getCurrentUser()
  const canAdjust = user?.role === 'super_admin'

  // Resolve user's branch
  const userBranchId =
    user?.selectedBranch?._id ||
    user?.branchId ||
    user?.branch?._id ||
    user?.branch ||
    null
  const isManager = user?.role === 'manager'

  // State
  const [branches, setBranches] = useState([])
  const [selectedBranchId, setSelectedBranchId] = useState(userBranchId || '')
  const [reportData, setReportData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [adjustItem, setAdjustItem] = useState(null) // item for adjust modal

  // Load branches on mount
  useEffect(() => {
    const load = async () => {
      try {
        const res = await BranchService.getAllBranches()
        const list = res?.data?.branches || res?.data || []
        setBranches(list.filter((b) => b.isActive))
      } catch (err) {
        console.error('NegativeStockReport: failed to load branches', err)
      }
    }
    load()
  }, [])

  // Auto-lock branch for managers
  useEffect(() => {
    if (isManager && userBranchId && !selectedBranchId) {
      setSelectedBranchId(userBranchId)
    }
  }, [isManager, userBranchId, selectedBranchId])

  // Fetch report
  const fetchReport = useCallback(async () => {
    if (!selectedBranchId) return
    setLoading(true)
    try {
      const res = await getNegativeStockReport(selectedBranchId)
      if (res?.success) {
        setReportData(res.data)
      } else {
        toast.error(res?.message || 'Failed to load negative stock report')
      }
    } catch (err) {
      toast.error(err?.message || 'Failed to load report')
    } finally {
      setLoading(false)
    }
  }, [selectedBranchId])

  // Auto-fetch when branch changes
  useEffect(() => {
    if (selectedBranchId) fetchReport()
  }, [fetchReport, selectedBranchId])

  const summary = reportData?.summary || null
  const negativeProducts = reportData?.negativeProducts || []
  const negativeRawMaterials = reportData?.negativeRawMaterials || []
  const recentOrders = reportData?.recentForcePunchedOrders || []

  const openAdjustModal = (type, id, name, currentStock, unit) => {
    setAdjustItem({ type, id, name, currentStock, unit, branchId: selectedBranchId })
  }

  return (
    <div className="p-6 max-w-[1400px] mx-auto space-y-6">
      {/* ── Page header ────────────────────────────────────────────────── */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <TrendingDown className="w-6 h-6 text-red-500" />
            Negative Stock Report
          </h1>
          {reportData?.generatedAt && (
            <p className="text-xs text-gray-400 mt-1 flex items-center gap-1">
              <Clock className="w-3 h-3" />
              Generated: {fmtDate(reportData.generatedAt)}
            </p>
          )}
        </div>

        <div className="flex items-center gap-3">
          {/* Branch selector */}
          {isManager ? (
            <div className="px-3 py-2 bg-gray-100 rounded-lg text-sm font-medium text-gray-700">
              {branches.find((b) => b._id === userBranchId)?.name || 'My Branch'}
            </div>
          ) : (
            <div className="relative">
              <select
                id="neg-stock-branch-select"
                value={selectedBranchId}
                onChange={(e) => setSelectedBranchId(e.target.value)}
                className="appearance-none pl-3 pr-9 py-2 bg-white border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-orange-400"
              >
                <option value="">Select Branch…</option>
                {branches.map((b) => (
                  <option key={b._id} value={b._id}>
                    {b.name}
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            </div>
          )}

          {/* Refresh */}
          <button
            id="neg-stock-refresh-btn"
            onClick={fetchReport}
            disabled={loading || !selectedBranchId}
            className="flex items-center gap-2 px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white text-sm font-semibold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* No branch selected */}
      {!selectedBranchId && (
        <div className="bg-orange-50 border border-orange-200 rounded-xl p-8 text-center">
          <AlertTriangle className="w-8 h-8 text-orange-400 mx-auto mb-2" />
          <p className="text-sm text-orange-700 font-medium">
            Please select a branch to load the report.
          </p>
        </div>
      )}

      {/* Loading skeleton */}
      {loading && (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-8 h-8 text-orange-500 animate-spin" />
          <span className="ml-3 text-gray-500">Loading report…</span>
        </div>
      )}

      {/* Report content */}
      {!loading && reportData && (
        <>
          {/* Summary cards */}
          <SummaryCards summary={summary} />

          {/* ── Table 1: Negative Products ─────────────────────────────── */}
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-2">
              <Package className="w-5 h-5 text-red-500" />
              <h2 className="font-semibold text-gray-900">Negative Products</h2>
              {negativeProducts.length > 0 && (
                <span className="ml-auto px-2.5 py-0.5 bg-red-100 text-red-700 text-xs font-bold rounded-full">
                  {negativeProducts.length}
                </span>
              )}
            </div>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr>
                    <th className={thClass}>Product</th>
                    <th className={thClass}>SKU</th>
                    <th className={thClass}>Category</th>
                    <th className={thClass}>Current Stock</th>
                    <th className={thClass}>Last Force-Punch By</th>
                    <th className={thClass}>Punched At</th>
                    {canAdjust && <th className={thClass}>Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {negativeProducts.length === 0 ? (
                    <EmptyRow cols={canAdjust ? 7 : 6} message="No products with negative stock 🎉" />
                  ) : (
                    negativeProducts.map((item, idx) => {
                      const prod = item.product || {}
                      const fp = item.forcePunchInfo || {}
                      const unit = prod.unit?.abbreviation || prod.unit?.name || ''
                      return (
                        <tr key={prod._id || idx} className="hover:bg-gray-50 transition-colors">
                          <td className={tdClass}>
                            <span className="font-medium text-gray-900">{prod.name || '—'}</span>
                          </td>
                          <td className={tdClass}>
                            <span className="font-mono text-xs bg-gray-100 px-2 py-0.5 rounded">
                              {prod.sku || '—'}
                            </span>
                          </td>
                          <td className={tdClass}>{prod.category?.name || '—'}</td>
                          <td className={tdClass}>
                            <span className="font-bold text-red-600 text-base">
                              {item.currentStock} {unit}
                            </span>
                          </td>
                          <td className={tdClass}>
                            {fp.forcePunchBy ? (
                              <div className="flex items-center gap-2">
                                <span className="font-medium">{fp.forcePunchBy.username}</span>
                                <RoleBadge role={fp.forcePunchBy.role} />
                              </div>
                            ) : (
                              '—'
                            )}
                          </td>
                          <td className={tdClass}>
                            <span className="text-xs text-gray-500">{fmtDate(fp.forcePunchAt)}</span>
                          </td>
                          {canAdjust && (
                            <td className={tdClass}>
                              <button
                                id={`adjust-product-btn-${prod._id}`}
                                onClick={() =>
                                  openAdjustModal('product', prod._id, prod.name, item.currentStock, prod.unit)
                                }
                                className="px-3 py-1.5 bg-orange-500 hover:bg-orange-600 text-white text-xs font-semibold rounded-lg transition-colors"
                              >
                                Adjust Stock
                              </button>
                            </td>
                          )}
                        </tr>
                      )
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* ── Table 2: Negative Raw Materials ───────────────────────── */}
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-2">
              <FlaskConical className="w-5 h-5 text-orange-500" />
              <h2 className="font-semibold text-gray-900">Negative Raw Materials</h2>
              {negativeRawMaterials.length > 0 && (
                <span className="ml-auto px-2.5 py-0.5 bg-orange-100 text-orange-700 text-xs font-bold rounded-full">
                  {negativeRawMaterials.length}
                </span>
              )}
            </div>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr>
                    <th className={thClass}>Raw Material</th>
                    <th className={thClass}>Code</th>
                    <th className={thClass}>Unit</th>
                    <th className={thClass}>Current Stock</th>
                    {canAdjust && <th className={thClass}>Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {negativeRawMaterials.length === 0 ? (
                    <EmptyRow cols={canAdjust ? 5 : 4} message="No raw materials with negative stock 🎉" />
                  ) : (
                    negativeRawMaterials.map((item, idx) => {
                      const rm = item.rawMaterial || {}
                      const unit = rm.unit?.abbreviation || rm.unit?.name || ''
                      return (
                        <tr key={rm._id || idx} className="hover:bg-gray-50 transition-colors">
                          <td className={tdClass}>
                            <span className="font-medium text-gray-900">{rm.name || '—'}</span>
                          </td>
                          <td className={tdClass}>
                            <span className="font-mono text-xs bg-gray-100 px-2 py-0.5 rounded">
                              {rm.code || '—'}
                            </span>
                          </td>
                          <td className={tdClass}>{unit || '—'}</td>
                          <td className={tdClass}>
                            <span className="font-bold text-red-600 text-base">
                              {item.currentStock} {unit}
                            </span>
                          </td>
                          {canAdjust && (
                            <td className={tdClass}>
                              <button
                                id={`adjust-rawmat-btn-${rm._id}`}
                                onClick={() =>
                                  openAdjustModal('rawMaterial', rm._id, rm.name, item.currentStock, rm.unit)
                                }
                                className="px-3 py-1.5 bg-orange-500 hover:bg-orange-600 text-white text-xs font-semibold rounded-lg transition-colors"
                              >
                                Adjust Stock
                              </button>
                            </td>
                          )}
                        </tr>
                      )
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* ── Table 3: Recent Force-Punched Orders ──────────────────── */}
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-2">
              <Zap className="w-5 h-5 text-violet-500" />
              <h2 className="font-semibold text-gray-900">Recent Force-Punched Orders</h2>
              {recentOrders.length > 0 && (
                <span className="ml-auto px-2.5 py-0.5 bg-violet-100 text-violet-700 text-xs font-bold rounded-full">
                  {recentOrders.length}
                </span>
              )}
            </div>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr>
                    <th className={thClass}>Order #</th>
                    <th className={thClass}>Items</th>
                    <th className={thClass}>Punched By</th>
                    <th className={thClass}>Punched At</th>
                  </tr>
                </thead>
                <tbody>
                  {recentOrders.length === 0 ? (
                    <EmptyRow cols={4} message="No recent force-punched orders" />
                  ) : (
                    recentOrders.map((order, idx) => (
                      <tr key={order.orderNumber || idx} className="hover:bg-gray-50 transition-colors">
                        <td className={tdClass}>
                          <span className="font-mono font-semibold text-violet-700">
                            {order.orderNumber || '—'}
                          </span>
                        </td>
                        <td className={tdClass}>
                          <div className="flex flex-wrap gap-1">
                            {(order.orderItems || []).map((oi, i) => (
                              <span
                                key={i}
                                className="inline-block bg-gray-100 text-gray-600 text-xs px-2 py-0.5 rounded-full"
                              >
                                {oi.product?.name || '?'} × {oi.quantity}
                              </span>
                            ))}
                          </div>
                        </td>
                        <td className={tdClass}>
                          {order.forcePunchBy ? (
                            <div className="flex items-center gap-2">
                              <span className="font-medium">{order.forcePunchBy.username}</span>
                              <RoleBadge role={order.forcePunchBy.role} />
                            </div>
                          ) : (
                            '—'
                          )}
                        </td>
                        <td className={tdClass}>
                          <span className="text-xs text-gray-500">{fmtDate(order.forcePunchAt)}</span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* Adjust Stock Modal */}
      <AdjustStockModal
        isOpen={!!adjustItem}
        onClose={() => setAdjustItem(null)}
        item={adjustItem}
        onSuccess={fetchReport}
      />
    </div>
  )
}

export default NegativeStockReport
